import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Rating as MuiRating } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useConsultation } from '../../contexts/ConsultationContext';
import { useAnalytics } from '../../analytics/hooks';
import CallConsentModal from '../consultation/CallConsentModal';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 20px;
  background: #FFFFFF;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(139, 105, 20, 0.08);
  border: 1px solid rgba(218, 165, 32, 0.08);

  @media (max-width: 600px) {
    margin: 16px;
    padding: 16px;
  }
`;

const HeaderSection = styled.div`
  display: flex;
  gap: 30px;
  margin-bottom: 30px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 16px;
  }
`;

const ProfileImage = styled.img`
  width: 120px;
  height: 150px;
  border-radius: 10px;
  object-fit: cover;
  border: 3px solid rgba(218, 165, 32, 0.2);
  box-shadow: 0 4px 12px rgba(139, 105, 20, 0.1);

  @media (max-width: 600px) {
    width: 90px;
    height: 112px;
  }
`;

const Info = styled.div`
  flex: 1;
`;

const Name = styled.h2`
  margin: 0 0 10px 0;
  color: #2C1810;
  font-family: 'Playfair Display', Georgia, serif;
`;

const Specialization = styled.p`
  color: #6B5744;
  margin: 0 0 6px;
`;

const RatingLine = styled.p`
  color: #DAA520;
  margin: 0 0 6px;
  font-size: 0.9rem;
`;

const Languages = styled.p`
  color: #9B8B7A;
  margin: 0;
  font-size: 0.85rem;
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 16px;
  margin: 20px 0;
`;

const Stat = styled.div`
  text-align: center;
  padding: 14px;
  background: #FFFDF7;
  border-radius: 12px;
  border: 1px solid rgba(218, 165, 32, 0.08);

  h4 {
    margin: 0 0 4px;
    color: #9B8B7A;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  p {
    margin: 0;
    color: #2C1810;
    font-weight: 700;
    font-size: 1rem;
  }
`;

const AboutSection = styled.div`
  margin: 24px 0;
  h3 {
    color: #2C1810;
    font-family: 'Playfair Display', Georgia, serif;
    margin-bottom: 8px;
  }
  p {
    color: #6B5744;
    line-height: 1.7;
  }
`;

/* ─── Action Buttons ─── */
const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-top: 24px;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const ActionButton = styled.button`
  padding: 14px 16px;
  border: none;
  border-radius: 12px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Inter', Arial, sans-serif;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }
`;

const ChatButton = styled(ActionButton)`
  background: linear-gradient(135deg, #2196f3, #1976d2);
  color: #fff;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(33, 150, 243, 0.35);
  }
`;

const CallButton = styled(ActionButton)`
  background: linear-gradient(135deg, #4caf50, #388e3c);
  color: #fff;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.35);
  }
`;

const VideoButton = styled(ActionButton)`
  background: linear-gradient(135deg, #DAA520, #B8860B);
  color: #fff;
  &:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(218, 165, 32, 0.35);
  }
`;

const RateTag = styled.span`
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.85;
`;

const OfflineTag = styled.div`
  text-align: center;
  color: #9B8B7A;
  font-size: 0.8rem;
  margin-top: 8px;
`;

const ReviewsSection = styled.div`
  margin-top: 30px;
  h3 {
    color: #2C1810;
    font-family: 'Playfair Display', Georgia, serif;
  }
`;

const ReviewCard = styled.div`
  padding: 15px;
  border-bottom: 1px solid rgba(218, 165, 32, 0.08);
  &:last-child { border-bottom: none; }
`;

const ReviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const AstrologerProfile = () => {
  const [astrologer, setAstrologer] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [pendingCallType, setPendingCallType] = useState(null);
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { startInstantConsultation } = useConsultation();
  const { track } = useAnalytics();

  useEffect(() => {
    if (id) {
      track('astrologer_viewed', { astrologer_id: id, source: 'profile' });
    }
    fetchAstrologer();
    fetchReviews();
  }, [id, track]);

  const fetchAstrologer = async () => {
    try {
      const response = await fetch(`/api/v1/astrologers/${id}`);
      const data = await response.json();
      setAstrologer(data.astrologer);
    } catch (error) {
      console.error('Error fetching astrologer:', error);
    }
  };

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await fetch(`/api/v1/astrologers/${id}/reviews?limit=10`);
      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleConsultation = (type) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // For call/video, check consent
    if (type !== 'chat' && !user?.callConsent) {
      setPendingCallType(type);
      setShowConsent(true);
      return;
    }
    navigateToConsultation(type);
  };

  const navigateToConsultation = (type) => {
    navigate('/talk-to-astrologer', {
      state: { selectedAstrologerId: astrologer.id, selectedType: type }
    });
  };

  const handleConsentGranted = () => {
    setShowConsent(false);
    if (pendingCallType) {
      navigateToConsultation(pendingCallType);
      setPendingCallType(null);
    }
  };

  if (!astrologer) return <div style={{ textAlign: 'center', padding: '3rem', color: '#9B8B7A' }}>Loading...</div>;

  const isOnline = astrologer.is_online || astrologer.online_status === 'online';
  const chatRate = astrologer.price_per_minute_chat || 10;
  const callRate = astrologer.price_per_minute_call || 20;
  const videoRate = astrologer.price_per_minute_video || 30;

  return (
    <ProfileContainer>
      <HeaderSection>
        <ProfileImage src={astrologer.profile_image || '/default-avatar.png'} alt={astrologer.name} />
        <Info>
          <Name>{astrologer.name}</Name>
          <Specialization>{astrologer.specialization}</Specialization>
          <RatingLine>
            {'★'.repeat(Math.round(astrologer.rating || 0))}{'☆'.repeat(5 - Math.round(astrologer.rating || 0))}
            {' '}{(astrologer.rating || 0).toFixed(1)} ({astrologer.total_consultations || 0} consultations)
          </RatingLine>
          {astrologer.languages && (
            <Languages>Languages: {Array.isArray(astrologer.languages) ? astrologer.languages.join(', ') : astrologer.languages}</Languages>
          )}
        </Info>
      </HeaderSection>

      <Stats>
        <Stat>
          <h4>Experience</h4>
          <p>{astrologer.experience_years} years</p>
        </Stat>
        <Stat>
          <h4>Chat Rate</h4>
          <p>₹{chatRate}/min</p>
        </Stat>
        <Stat>
          <h4>Call Rate</h4>
          <p>₹{callRate}/min</p>
        </Stat>
        {videoRate > 0 && (
          <Stat>
            <h4>Video Rate</h4>
            <p>₹{videoRate}/min</p>
          </Stat>
        )}
      </Stats>

      <AboutSection>
        <h3>About</h3>
        <p>{astrologer.bio}</p>
      </AboutSection>

      {/* Action Buttons: Chat, Call, Video */}
      <ActionRow>
        <ChatButton
          onClick={() => handleConsultation('chat')}
          disabled={!isOnline}
        >
          💬 Chat Now <RateTag>₹{chatRate}/min</RateTag>
        </ChatButton>
        <CallButton
          onClick={() => handleConsultation('call')}
          disabled={!isOnline}
        >
          📞 Voice Call <RateTag>₹{callRate}/min</RateTag>
        </CallButton>
        <VideoButton
          onClick={() => handleConsultation('video')}
          disabled={!isOnline}
        >
          📹 Video Call <RateTag>₹{videoRate}/min</RateTag>
        </VideoButton>
      </ActionRow>
      {!isOnline && <OfflineTag>This astrologer is currently offline</OfflineTag>}

      <ReviewsSection>
        <h3>Reviews</h3>
        {reviewsLoading && <p>Loading reviews...</p>}
        {!reviewsLoading && reviews.length === 0 && (
          <p style={{ color: '#9B8B7A' }}>No reviews yet.</p>
        )}
        {reviews.map((r) => (
          <ReviewCard key={r.consultation_id}>
            <ReviewHeader>
              <strong>{r.user_name}</strong>
              <MuiRating value={r.rating} readOnly size="small" />
            </ReviewHeader>
            {r.review && <p style={{ margin: 0, color: '#6B5744' }}>{r.review}</p>}
            <small style={{ color: '#9B8B7A' }}>
              {r.type} - {new Date(r.date).toLocaleDateString('en-IN')}
            </small>
          </ReviewCard>
        ))}
      </ReviewsSection>

      {showConsent && (
        <CallConsentModal
          onConsent={handleConsentGranted}
          onDecline={() => { setShowConsent(false); setPendingCallType(null); }}
        />
      )}
    </ProfileContainer>
  );
};

export default AstrologerProfile;
